import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createCheckoutPreference, sendOrderEmail } from '../api/checkout';
import { trackEvent } from '../utils/analytics';

// Order value/items are stashed here so the /success page can fire the
// "purchase" event with the right amount once the payment actually
// completes (begin_checkout fires here, before we know if it'll succeed).
export const LAST_ORDER_STORAGE_KEY = 'miestampita_last_order';

const CheckoutButton = ({
  poster,
  customerData,
  stampFormData,
  calculatedPrice,
  shippingCost,
  printSide,
  copies,
  termsAccepted,
}) => {
  const [loading, setLoading] = useState(false);
  // El backend puede estar "dormido" (free tier de Render) y tardar hasta
  // ~50s en despertar. Si el pedido tarda más de lo normal, avisamos para
  // que el usuario no piense que la página se colgó y cierre/reintente.
  const [slowLoading, setSlowLoading] = useState(false);

  const handleCheckout = async () => {
    if (!termsAccepted) {
      alert('Debes aceptar los Términos y Condiciones para continuar.');
      return;
    }

    if (!poster || !poster.selectedVariant) {
      alert('Por favor seleccioná una impresión antes de continuar.');
      return;
    }

    if (!customerData?.isValid || !stampFormData?.isSaved) {
      alert('Completá los datos de personalización y envío antes de continuar.');
      return;
    }

    setLoading(true); // Empieza el loading
    setSlowLoading(false);
    const slowLoadingTimer = setTimeout(() => setSlowLoading(true), 4000);

    const items = [
      {
        title: `${poster.title} - ${poster.selectedVariant.type || poster.selectedVariant.label}`,
        quantity: 1,
        unit_price: calculatedPrice,
        currency_id: 'UYU',
      },
      {
        title: 'Envío',
        quantity: 1,
        unit_price: shippingCost,
        currency_id: 'UYU',
      },
    ];
    const emailItems = [
      {
        name: `${poster.title} - ${poster.selectedVariant.type || poster.selectedVariant.label}`,
        qty: 1,
        price: calculatedPrice,
      },
      {
        name: 'Envío',
        qty: 1,
        price: shippingCost,
      },
    ];
    const emailTotal = calculatedPrice + shippingCost;

    const ecommerceItems = [
      {
        item_id: poster.slug,
        item_name: poster.title,
        item_category: poster.category,
        price: calculatedPrice / (copies || 1),
        quantity: copies,
      },
    ];

    trackEvent('begin_checkout', {
      currency: 'UYU',
      value: emailTotal,
      items: ecommerceItems,
    });

    try {
      sessionStorage.setItem(
        LAST_ORDER_STORAGE_KEY,
        JSON.stringify({ currency: 'UYU', value: emailTotal, items: ecommerceItems }),
      );
    } catch {
      // sessionStorage can be unavailable (e.g. private browsing); the
      // /success page just won't have order details for the purchase event.
    }

    const printSideLabel = printSide === 'single' ? 'Solo un lado ($45 c/u)' : 'Ambos lados ($65 c/u)';
    const generoLabel = stampFormData?.genero === 'nino'
      ? 'Varón'
      : stampFormData?.genero === 'nina'
        ? 'Niña'
        : '';
    const emailNotes = [
      generoLabel ? `Género: ${generoLabel}` : '',
      stampFormData?.name ? `Nombre homenajeado: ${stampFormData.name}` : '',
      stampFormData?.date ? `Fecha: ${stampFormData.date}` : '',
      stampFormData?.capilla ? `Capilla: ${stampFormData.capilla}` : '',
      stampFormData?.madrina ? `Nombre de la madrina: ${stampFormData.madrina}` : '',
      stampFormData?.padrino ? `Nombre del padrino: ${stampFormData.padrino}` : '',
      stampFormData?.text ? stampFormData.text : '',
      `Impresión: ${printSideLabel}`,
    ]
      .filter(Boolean)
      .join('\n');

    const fullCustomerAddress = [
      customerData?.address,
      customerData?.city,
      customerData?.department,
      customerData?.postalCode ? `CP ${customerData.postalCode}` : '',
    ]
      .filter(Boolean)
      .join(', ');

    try {
      // El email se envía en paralelo con la creación de la preferencia:
      // si el envío del email falla o demora, no debe bloquear ni retrasar
      // la apertura de la pantalla de pago de Mercado Pago.
      const [emailResult, preferenceResult] = await Promise.allSettled([
        sendOrderEmail({
          customerName: customerData?.name,
          customerAddress: fullCustomerAddress,
          customerPhone: customerData?.phone,
          customerEmail: customerData?.email,
          stampName: stampFormData?.name,
          stampDate: stampFormData?.date,
          items: emailItems,
          total: emailTotal,
          notes: emailNotes,
        }),
        createCheckoutPreference({
          items,
          customer: customerData,
          customization: {
            ...stampFormData,
            printSide,
            copies,
          },
        }),
      ]);

      if (emailResult.status === 'rejected') {
        console.error('Error enviando el email de la orden:', emailResult.reason);
      }

      if (preferenceResult.status === 'fulfilled' && preferenceResult.value?.init_point) {
        window.location.href = preferenceResult.value.init_point;
      } else {
        alert('No se pudo generar el enlace de pago.');
      }
    } catch (error) {
      console.error('Error en checkout:', error);
      alert(error?.message || 'Ocurrió un error en la conexión.');
    } finally {
      clearTimeout(slowLoadingTimer);
      setSlowLoading(false);
      setLoading(false);
    }
  };

  return (
    <div className="checkout-button-wrapper">
      <button
        className="checkout-button"
        onClick={handleCheckout}
        disabled={loading || !poster || !poster.selectedVariant || !termsAccepted}
      >
        {loading ? (slowLoading ? 'Esto puede tardar unos segundos…' : 'Cargando...') : 'Confirmar Pedido'}
      </button>
      {slowLoading && (
        <p className="checkout-button-hint">
          El servidor puede tardar unos segundos en responder. No cierres ni recargues la página.
        </p>
      )}
    </div>
  );
};

CheckoutButton.propTypes = {
  poster: PropTypes.shape({
    title: PropTypes.string.isRequired,
    selectedVariant: PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string,
      price: PropTypes.number.isRequired,
    }),
  }),
  calculatedPrice: PropTypes.number.isRequired,
  shippingCost: PropTypes.number.isRequired,
  printSide: PropTypes.oneOf(['double', 'single']).isRequired,
  copies: PropTypes.number.isRequired,
  termsAccepted: PropTypes.bool.isRequired,
  customerData: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    department: PropTypes.string,
    postalCode: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    isValid: PropTypes.bool,
  }),
  stampFormData: PropTypes.shape({
    genero: PropTypes.string,
    name: PropTypes.string,
    date: PropTypes.string,
    text: PropTypes.string,
    capilla: PropTypes.string,
    madrina: PropTypes.string,
    padrino: PropTypes.string,
    isSaved: PropTypes.bool,
  }),
};

CheckoutButton.defaultProps = {
  poster: null,
  customerData: null,
  stampFormData: null,
};

export default CheckoutButton;
